import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { useHeaderShowVersionFlag } from '@/hooks/useHeaderShowVersionFlag'
import {
  APP_SETTINGS_EMAIL_VERIFICATION_BYPASS_KEY,
  APP_SETTINGS_HEADER_SHOW_VERSION_KEY,
  APP_SETTINGS_GROUPS_FEED_SHOW_VENDOR_GROUPS_KEY,
  APP_SETTINGS_LISTINGS_SHOW_SIGNED_PROGRAMS_KEY,
  APP_SETTINGS_QUERY_KEY,
} from '@/config/appSettingsKeys'
import { useEmailVerificationBypassFlag } from '@/hooks/useEmailVerificationBypassFlag'
import { useGroupsFeedShowVendorGroupsFlag } from '@/hooks/useGroupsFeedShowVendorGroupsFlag'
import { useListingsShowSignedProgramsFlag } from '@/hooks/useListingsShowSignedProgramsFlag'
import { TV_DATA_QUERY_KEY } from '@/hooks/useTvData'

export default function AdminFeatureFlagsPage() {
  const navigate = useNavigate()
  const isAdmin = useAdmin()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { headerShowVersion, isLoading: loadingVersion } = useHeaderShowVersionFlag()
  const { showVendorGroupsInFeed, isLoading: loadingVendorGroups } = useGroupsFeedShowVendorGroupsFlag()
  const { showSignedInListings, isLoading: loadingSignedListings } = useListingsShowSignedProgramsFlag()
  const { emailVerificationBypass, isLoading: loadingEmailBypass } = useEmailVerificationBypassFlag()
  const isLoading = loadingVersion || loadingVendorGroups || loadingSignedListings || loadingEmailBypass

  useEffect(() => {
    if (isAdmin === false) {
      navigate('/', { replace: true })
    }
  }, [isAdmin, navigate])

  const setHeaderShowVersion = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.from('app_settings').upsert(
        {
          key: APP_SETTINGS_HEADER_SHOW_VERSION_KEY,
          value: enabled ? 'true' : 'false',
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        },
        { onConflict: 'key' }
      )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
    },
  })

  const setGroupsFeedShowVendorGroups = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.from('app_settings').upsert(
        {
          key: APP_SETTINGS_GROUPS_FEED_SHOW_VENDOR_GROUPS_KEY,
          value: enabled ? 'true' : 'false',
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        },
        { onConflict: 'key' }
      )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
    },
  })

  const setListingsShowSignedPrograms = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.from('app_settings').upsert(
        {
          key: APP_SETTINGS_LISTINGS_SHOW_SIGNED_PROGRAMS_KEY,
          value: enabled ? 'true' : 'false',
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        },
        { onConflict: 'key' }
      )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
    },
  })

  const setEmailVerificationBypass = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.from('app_settings').upsert(
        {
          key: APP_SETTINGS_EMAIL_VERIFICATION_BYPASS_KEY,
          value: enabled ? 'true' : 'false',
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        },
        { onConflict: 'key' }
      )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
    },
  })

  if (isAdmin === false) return null

  return (
    <div className="max-w-3xl">
      <h1 id="feature-flags-heading" className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
        Feature flags
      </h1>
      <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
        Global settings that affect how the platform behaves. Changes apply to all users.
      </p>

      {isLoading ? (
        <p role="status" aria-live="polite" style={{ color: 'var(--text-secondary)' }}>
          Loading…
        </p>
      ) : (
        <>
        <section
          className="p-6 rounded-lg border-2"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
          aria-labelledby="show-version-heading"
        >
          <h2 id="show-version-heading" className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Show version in header
          </h2>
          <label
            className="flex items-center gap-2 cursor-pointer"
            style={{ color: 'var(--text-primary)' }}
          >
            <input
              type="checkbox"
              checked={headerShowVersion}
              onChange={(e) => setHeaderShowVersion.mutate(e.target.checked)}
              disabled={setHeaderShowVersion.isPending}
              className="w-4 h-4"
              aria-describedby="show-version-hint"
            />
            <span className="font-semibold">Show version in header</span>
          </label>
          <p
            id="show-version-hint"
            className="text-sm mt-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            When enabled, the repo commit ID and commit date appear under the logo in the header for moderators and admins.
          </p>
          {setHeaderShowVersion.isError && (
            <p role="alert" className="text-sm mt-2" style={{ color: 'var(--color-error)' }}>
              Failed to save: {setHeaderShowVersion.error instanceof Error ? setHeaderShowVersion.error.message : String(setHeaderShowVersion.error)}
            </p>
          )}
        </section>

        <section
          className="p-6 rounded-lg border-2 mt-6"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
          aria-labelledby="vendor-groups-heading"
        >
          <h2 id="vendor-groups-heading" className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Show vendor groups in main group feed
          </h2>
          <label
            className="flex items-center gap-2 cursor-pointer"
            style={{ color: 'var(--text-primary)' }}
          >
            <input
              type="checkbox"
              checked={showVendorGroupsInFeed}
              onChange={(e) => setGroupsFeedShowVendorGroups.mutate(e.target.checked)}
              disabled={setGroupsFeedShowVendorGroups.isPending}
              className="w-4 h-4"
              aria-describedby="vendor-groups-hint"
            />
            <span className="font-semibold">Show vendor groups in main group feed</span>
          </label>
          <p
            id="vendor-groups-hint"
            className="text-sm mt-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            When enabled, groups created by broadcasters and ad-creators appear in the main Groups page (My groups and Other groups), in the join-group list, and in the activity sidebar. When disabled, only community groups are shown there.
          </p>
          {setGroupsFeedShowVendorGroups.isError && (
            <p role="alert" className="text-sm mt-2" style={{ color: 'var(--color-error)' }}>
              Failed to save: {setGroupsFeedShowVendorGroups.error instanceof Error ? setGroupsFeedShowVendorGroups.error.message : String(setGroupsFeedShowVendorGroups.error)}
            </p>
          )}
        </section>

        <section
          className="p-6 rounded-lg border-2 mt-6"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
          aria-labelledby="signed-listings-heading"
        >
          <h2 id="signed-listings-heading" className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Identify signed programmes in listings
          </h2>
          <label
            className="flex items-center gap-2 cursor-pointer"
            style={{ color: 'var(--text-primary)' }}
          >
            <input
              type="checkbox"
              checked={showSignedInListings}
              onChange={(e) => setListingsShowSignedPrograms.mutate(e.target.checked)}
              disabled={setListingsShowSignedPrograms.isPending}
              className="w-4 h-4"
              aria-describedby="signed-listings-hint"
            />
            <span className="font-semibold">Identify signed programmes in listings</span>
          </label>
          <p
            id="signed-listings-hint"
            className="text-sm mt-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            When enabled, the TV Listings screen identifies signed content (BSL) from broadcaster accessibility uploads and from programme titles prefixed with &quot;Signed:&quot;, and shows a BSL filter. When disabled, signed/BSL detection is not run and the BSL filter is hidden.
          </p>
          {setListingsShowSignedPrograms.isError && (
            <p role="alert" className="text-sm mt-2" style={{ color: 'var(--color-error)' }}>
              Failed to save: {setListingsShowSignedPrograms.error instanceof Error ? setListingsShowSignedPrograms.error.message : String(setListingsShowSignedPrograms.error)}
            </p>
          )}
        </section>

        <section
          className="p-6 rounded-lg border-2 mt-6"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
          aria-labelledby="email-verification-bypass-heading"
        >
          <h2 id="email-verification-bypass-heading" className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Bypass email verification
          </h2>
          <label
            className="flex items-center gap-2 cursor-pointer"
            style={{ color: 'var(--text-primary)' }}
          >
            <input
              type="checkbox"
              checked={emailVerificationBypass}
              onChange={(e) => setEmailVerificationBypass.mutate(e.target.checked)}
              disabled={setEmailVerificationBypass.isPending}
              className="w-4 h-4"
              aria-describedby="email-verification-bypass-hint"
            />
            <span className="font-semibold">Bypass email verification</span>
          </label>
          <p
            id="email-verification-bypass-hint"
            className="text-sm mt-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            When enabled, newly registered users are automatically confirmed and can sign in without clicking the verification link. Use when Supabase has email confirmation enabled but you want to skip it (e.g. for testing or internal use). Requires the <code className="text-xs">confirm-email-bypass</code> Edge Function to be deployed.
          </p>
          {setEmailVerificationBypass.isError && (
            <p role="alert" className="text-sm mt-2" style={{ color: 'var(--color-error)' }}>
              Failed to save: {setEmailVerificationBypass.error instanceof Error ? setEmailVerificationBypass.error.message : String(setEmailVerificationBypass.error)}
            </p>
          )}
        </section>
        </>
      )}
    </div>
  )
}
